import React from 'react';
import '../../App.scss';
import 'antd/dist/antd.css';
import {
  DollarCircleFilled, EuroCircleFilled, PoundCircleFilled , PlusCircleOutlined, PlusOutlined,
  WarningTwoTone, ExclamationCircleOutlined, CheckCircleFilled, CheckCircleOutlined, BankOutlined, EditOutlined,
  ClockCircleOutlined, BarChartOutlined, WalletOutlined, DownOutlined, UpOutlined, RightOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { List, Button, Space , Table, Tag, Typography, Badge, Row, Col, Alert, Tooltip, Modal } from 'antd';
import moment from 'moment';

import { ccyIsoCodeToSymbolMap } from '../../utils/CurrencyUtils';
import MoneyTitle from '../customer/MoneyTitle';
import ProductSwitcher from '../../utils/ProductSwitcher';

import { WithdrawalProgressListView } from './PayoutProgressListView';

// Hook to get last withdrawal for each account - reusing existing CashOutDialog logic
const useLastWithdrawalsByAccount = (clientId, accountIds, withdrawals = []) => {
  return React.useMemo(() => {
    if (!withdrawals || withdrawals.length === 0 || !accountIds || accountIds.length === 0) return {};
    
    const lastByAccount = {};
    
      accountIds.forEach(accountId => {
        // Filter withdrawals for this account (similar to CashOutDialog logic)
        const accountWithdrawals = withdrawals.filter(w => 
          w.accountId === accountId && w.payout
        );
        
        if (accountWithdrawals.length > 0) {
          // Sort by createdAt descending (same as CashOutDialog)
          const sortedWithdrawals = accountWithdrawals.sort((a, b) => {
            const dateA = a.createdAt && a.createdAt.toDate ? a.createdAt.toDate() : new Date(0);
            const dateB = b.createdAt && b.createdAt.toDate ? b.createdAt.toDate() : new Date(0);
            return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
          });
          
          lastByAccount[accountId] = sortedWithdrawals[0];
        }
      });
    
    return lastByAccount;
  }, [withdrawals, accountIds]);
};

// Last Withdrawal Box Component - Reusing WithdrawalsList structure
const LastWithdrawalBox = ({ withdrawal, clientId, isAdmin = false }) => {
  if (!withdrawal) {
    return null;
  }
  
  const { amount, currency, createdAt, payout } = withdrawal;
  const id = withdrawal.withdrawalId || withdrawal.id;
  const status = withdrawal.status;
  
  // Only show withdrawals from the last 7 days
  const withdrawalDate = createdAt ? (createdAt.toDate ? createdAt.toDate() : createdAt) : null;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  
  if (withdrawalDate && withdrawalDate < sevenDaysAgo) {
    return null; // Don't show withdrawals older than 7 days
  }
  
  const amountNode = (amount, currency, status) => (
    <Title level={4} style={{ margin: 0, color: '#000', fontSize: '18px' }}>
      <MoneyTitle amount={amount} currency={currency} digits={2} strikethrough={reversedStatuses.includes(status)} />
    </Title>
  );

  const bankTitle = payout && payout.displayName ? payout.displayName : 'Bank Account';
  const short = payout && payout.accountNumber ? `***${String(payout.accountNumber).slice(-4)}` : (payout && payout.iban ? `***${String(payout.iban).slice(-4)}` : '');
  
  return (
    <Link 
      to={isAdmin ? 
        `/admin/clients/${clientId}/bank?returnTo=history&highlight=${id}` : 
        `/client/manage/${clientId}/bank?returnTo=history&highlight=${id}`
      }
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <div style={{ 
        position: 'relative',
        marginTop: 12, 
        padding: 16, 
        backgroundColor: '#ffffff', 
        borderRadius: 8,
        border: '2px solid #e6f7ff',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        ':hover': {
          backgroundColor: '#f0f9ff',
          borderColor: '#1890ff'
        }
      }}>
        {/* Folder-style tab */}
        <div style={{
          position: 'absolute',
          top: '-2px',
          left: window.innerWidth <= 576 ? '12px' : '16px',
          backgroundColor: '#1890ff',
          color: '#ffffff',
          fontSize: window.innerWidth <= 576 ? '11px' : '12px',
          fontWeight: 'bold',
          padding: window.innerWidth <= 576 ? '3px 8px' : '4px 12px',
          borderRadius: '0 0 6px 6px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: 1,
          border: '2px solid #e6f7ff',
          borderBottom: 'none'
        }}>
          Latest cash out
        </div>
        {/* Bootstrap-style flexbox layout: Amount | Account Info | Action */}
        <div style={{ 
          display: 'flex', 
          flexDirection: window.innerWidth <= 576 ? 'column' : 'row',
          alignItems: window.innerWidth <= 576 ? 'stretch' : 'center', 
          justifyContent: window.innerWidth <= 576 ? 'flex-start' : 'space-between',
          width: '100%',
          gap: window.innerWidth <= 576 ? '12px' : '16px',
          paddingTop: '20px' // Add spacing so tab doesn't obscure content
        }}>
          {/* Column 1: Amount + Status Tag (PRIORITY) */}
          <div style={{ 
            flex: window.innerWidth <= 576 ? 'none' : '1',
            width: window.innerWidth <= 576 ? '100%' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '4px' }}>
              {amountNode(amount, currency, status)}
            </div>
            <div style={{ marginBottom: '4px' }}>
              <Tag 
                color={
                  status === 'paid' ? '#52c41a' :
                  status === 'sent' || status === 'batch_requested' || status === 'batched' ? 'blue' :
                  status === 'refunded' ? '#ff4d4f' :
                  status === 'refund_requested' ? 'orange' :
                  status === 'failed' ? '#ff4d4f' :
                  'default'
                }
                style={{ fontSize: '11px' }}
              >
                {status === 'paid' ? 'Paid' :
                 status === 'sent' || status === 'batch_requested' || status === 'batched' ? 'Sent' :
                 status === 'refunded' ? 'Refunded' :
                 status === 'refund_requested' ? 'Refunding...' :
                 status === 'failed' ? 'Failed' :
                 'In progress'}
              </Tag>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: '10px', color: '#999' }}>
                {(() => {
                  let dateField;
                  if (status === 'paid') {
                    dateField = withdrawal.paidAt || withdrawal.updatedAt;
                  } else if (status === 'sent' || status === 'batch_requested' || status === 'batched') {
                    dateField = withdrawal.sentAt || withdrawal.createdAt;
                  } else if (status === 'refunded') {
                    dateField = withdrawal.refundedAt || withdrawal.updatedAt;
                  } else if (status === 'refund_requested') {
                    dateField = withdrawal.refundedAt || withdrawal.updatedAt;
                  } else if (status === 'failed') {
                    dateField = withdrawal.updatedAt || withdrawal.createdAt;
                  } else {
                    dateField = withdrawal.createdAt;
                  }
                  
                  if (dateField) {
                    const date = dateField.toDate ? dateField.toDate() : dateField;
                    return moment(date).format('MMM D, YYYY');
                  }
                  return '';
                })()}
              </Text>
            </div>
          </div>
          
          {/* Column 2: Account + Reference + Status Date */}
          <div style={{ 
            flex: window.innerWidth <= 576 ? 'none' : '1',
            width: window.innerWidth <= 576 ? '100%' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '2px' }}>
              <Space>
                {statusDot(status)}
                <Text strong>{`${bankTitle} ${short}`}</Text>
              </Space>
            </div>
            <div style={{ marginBottom: '2px' }}>
              <Tag style={{ 
                background: '#fff', 
                borderColor: '#d9d9d9', 
                color: '#000', 
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: '11px',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {`Strikepay - ${id}`}
              </Tag>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11, color: '#666', textAlign: 'center' }}>
                {getStatusDateDisplay(withdrawal)}
              </Text>
            </div>
          </div>
          
          {/* Column 3: Click indicator */}
          <div style={{ 
            flex: window.innerWidth <= 576 ? 'none' : '1',
            width: window.innerWidth <= 576 ? '100%' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          }}>
            <Text type="secondary" style={{ fontSize: '12px', color: '#1890ff' }}>
              View Details →
            </Text>
          </div>
        </div>
      </div>
    </Link>
  );
};

const { Text, Paragraph, Title } = Typography;
const ccy = ccyIsoCodeToSymbolMap;

// Helper function to add business days (excluding weekends)
const addBusinessDays = (date, days) => {
  const result = new Date(date);
  let addedDays = 0;
  
  while (addedDays < days) {
    result.setDate(result.getDate() + 1);
    // Skip weekends (Saturday = 6, Sunday = 0)
    if (result.getDay() !== 0 && result.getDay() !== 6) {
      addedDays++;
    }
  }
  
  return result;
};

// Helper function to get status-based date display
const getStatusDateDisplay = (withdrawal) => {
  const { status, refundedAt, paidAt, updatedAt, createdAt } = withdrawal;
  
  // Only show specific terminal state dates, everything else shows expected date
  switch (status) {
    case 'refunded':
      return refundedAt ? 
        `Refunded on: ${moment(refundedAt.toDate ? refundedAt.toDate() : refundedAt).format('ddd, MMM D, YYYY')}` : 
        'N/A';
    
    case 'paid':
      const paidDate = paidAt || updatedAt;
      return paidDate ? 
        `Paid on: ${moment(paidDate.toDate ? paidDate.toDate() : paidDate).format('ddd, MMM D, YYYY')}` : 
        'N/A';
    
    case 'failed':
      const failedDate = updatedAt || createdAt;
      return failedDate ? 
        `Failed on: ${moment(failedDate.toDate ? failedDate.toDate() : failedDate).format('ddd, MMM D, YYYY')}` : 
        'N/A';
    
    // All other states (sent, batch_requested, batched, refund_requested, waiting, etc.) show expected date
    default:
      return createdAt ? 
        `Expected by: ${moment(addBusinessDays(createdAt.toDate ? createdAt.toDate() : createdAt, 3)).format('ddd, MMM D, YYYY')}` : 
        'N/A';
  }
};

const ccy_icons = {
  'EUR': <EuroCircleFilled style={{ fontSize: '36px', color: '#00c' }}/>,
  'USD': <DollarCircleFilled  style={{ fontSize: '36px', color: '#08c' }}/>,
  'GBP': <PoundCircleFilled  style={{ fontSize: '36px', color: '#70c' }}/>,
};

const allowedStatuses = [
  'sent', 'paid', 'api_requested', 'waiting', 'batch_requested', 'batched',
  'refund_requested', 'refund_failed', 'refunded',
]; // MARK: sent means batch

const reversedStatuses = [
  'refund_requested', 'refund_failed', 'refunded',
]; // refunded shows as reversed (strike-through)

function iconForCCY(ccy) {
  let icon = ccy_icons[ccy];
  return (icon) ? icon : <DollarCircleFilled  style={{ fontSize: '50px', color: '#08c' }}/>;
}

const makeBankDescription = (item) => {
  let description = "";
  if(item.iban) {
    description = `IBAN (${item.country}): ${item.iban}`;
  } else if(item.sortCode && item.accountNumber) {
    description = `Sort Code (${item.country}): ${item.sortCode} -- Account Number: ${item.accountNumber}`;
  } else if(item.routingNumber && item.accountNumber) {
    description = `${item.accountNumber} # ${item.routingNumber}`;
  } else if(item.provider && item.provider === 'moneyjar') {
    description = 'via MoneyJar App';
  }

  return description;
};

const makeShortBankDescription = (item) => {
  let description = "";
  if(item.iban) {
    description = `***${item.iban.slice(-4)}`;
  } else if(item.sortCode && item.accountNumber) {
    description = `***${item.accountNumber.slice(-4)}`;
  } else if(item.routingNumber && item.accountNumber) {
    description = `***${item.accountNumber.slice(-4)}`;
  } else if(item.provider && item.provider === 'moneyjar') {
    description = 'MoneyJar';
  }

  return description;
};

const makeDisplayName = (item) => {
  const title = `${item.displayName}${item.isCompany ? ' (Business)' : ''}`;
  const style = {
    width: '16px',
    height: '16px',
    backgroundColor: '#FF0098',
    borderRadius: '4px',
    padding: '2px',
    marginLeft: '6px',
    marginRight: '4px',
    marginBottom: '2px',
  };
  const isShowTL = item.source && item.source === 'truelayer' ? true : false;

  return (
    <>
      <span>{title}</span>
      {isShowTL &&
        <span >
          <img alt='truelayer' src="https://truelayer-public-assets.s3-eu-west-1.amazonaws.com/logo/mark/tl-white.svg"  style={{ ...style }} />
        </span>
      }
    </>
    );
};

const truncateString = (str, maxLength) => {
  if (str.length <= maxLength) {
      return str;
  }

  // Find the last space within the maxLength
  let truncated = str.slice(0, maxLength + 1);
  let lastSpaceIndex = truncated.lastIndexOf(' ');

  // If no space is found, return the original truncation
  if (lastSpaceIndex === -1) {
      return truncated.slice(0, maxLength);
  }

  // Return the truncated string up to the last space
  return truncated.slice(0, lastSpaceIndex);
}

/**
 * Check if user can see the Payouts button
 * @param {Object} client - Client object with balances, stripeConnectedAccounts, country
 * @returns {Object} { show: boolean, reason: string, message: string }
 */
export const canShowPayoutsButton = (client) => {
  if (!client) {
    return {
      show: false,
      reason: 'no_client',
      message: 'Loading client data...'
    };
  }

  const { balances, stripeConnectedAccounts, country } = client;
  
  // Check 1: Does client have ANY available balance?
  // If balances is missing, do not block UI (graceful fallback)
  const hasBalance = !balances ? true : Object.entries(balances).some(([currency, amount]) => {
    return amount > 0; // Amount in cents
  });
  
  if (!hasBalance) {
    return {
      show: false,
      reason: 'no_balance',
      message: 'No balance available to withdraw'
    };
  }
  
  // Check 2: Is this a US client?
  const isUSClient = country === 'US';
  
  // For US clients with balance, always allow access (Stripe account created during first cash out)
  // Only block if no balance
  
  // All checks passed
  return {
    show: true,
    reason: null,
    message: null
  };
};

/**
 * Check if user can add a bank account for a specific currency
 * @param {string} currency - Currency code (USD, EUR, GBP)
 * @param {Object} client - Client object
 * @returns {Object} { allowed: boolean, reason: string, message: string }
 */
/** The currencies a client can be paid in, before any balance exists. */
export const payoutCurrenciesFor = (client) => (
  client && client.country === 'US' ? ['USD'] : ['EUR', 'GBP']
);

export const placeholderAccountsFor = (client) => payoutCurrenciesFor(client).map((currency) => ({
  currency, available: 0, withdrawn: 0, product: 'tipping', placeholder: true,
}));

export const canAddBankAccount = (currency, client, existingPayouts = []) => {
  if (!client) {
    return {
      allowed: false,
      reason: 'no_client',
      message: 'Loading...'
    };
  }

  // Non-USD currencies (EUR, GBP): Non-US clients can add anytime
  if (currency !== 'USD') {
    if (client.country === 'US') {
      return {
        allowed: false,
        reason: 'country_restriction',
        message: 'US clients can only add USD bank accounts'
      };
    }
    
    // Non-US clients can always add EUR/GBP accounts
    return {
      allowed: true,
      reason: null,
      message: null
    };
  }
  
  // USD: Only US clients can add USD accounts
  if (client.country !== 'US') {
    return {
      allowed: false,
      reason: 'country_restriction',
      message: 'Only US clients can add USD bank accounts'
    };
  }
  
  // US client adding USD: allowed before any tip arrives. The bank account is
  // stored now; the Stripe side activates it on the first tip (the API records
  // "activated when you receive your first tip" as a retry, not a failure).
  return {
    allowed: true,
    reason: null,
    message: null
  };
};

/**
 * Hook: useBalancesByCurrency
 * Given an array of account objects (with currency, available),
 * returns a memoized map of currency -> total available amount (in cents)
 */
export const useBalancesByCurrency = (accounts) => {
  return React.useMemo(() => {
    if (!accounts || accounts.length === 0) return {};
    return accounts.reduce((acc, v) => {
      const curr = v && v.currency ? v.currency : null;
      if (!curr) return acc;
      const available = v && v.available ? v.available : 0;
      acc[curr] = (acc[curr] || 0) + available;
      return acc;
    }, {});
  }, [accounts]);
};

/**
 * Determines the display state for a payout based on status and severity
 * Returns: { state: 'in-progress' | 'error' | 'live', color, icon, message }
 */
const getPayoutDisplayState = (item) => {
  const { 
    status, 
    severity, 
    actionRequiredMessage, 
    lastErrorMessage, 
    stripeError,
    stripeAction,
    provider, 
    type 
  } = item;
  
  // Live state
  if (status === 'live') {
    return {
      state: 'live',
      statusText: 'Live',
      color: '#52c41a', // green
      dotColor: 'green',
      message: null,
      showDot: true,
    };
  }
  
  // Classic/generic accounts in 'ready' status are immediately active (no API activation needed)
  // These are accounts without a specific provider (stripeus, moneyjar, etc.)
  if (status === 'ready' && !provider && !type) {
    return {
      state: 'live',
      statusText: 'Active',
      color: '#52c41a', // green
      dotColor: 'green',
      message: null,
      showDot: true,
    };
  }
  
  // In-progress states (waiting, ready with info severity)
  if (status === 'waiting' || (status === 'retry' && severity === 'info')) {
    const message = actionRequiredMessage || 'No action needed.';
    return {
      state: 'in-progress',
      statusText: 'In Progress',
      color: '#1890ff', // blue
      dotColor: 'blue',
      message: message,
      showDot: true,
    };
  }
  
  // Error states (retry with error severity, or retry without severity info)
  if (status === 'retry') {
    // Use actionRequiredMessage for user-friendly message (saved to DB)
    // Fall back to stripeAction, errorMessage, or lastErrorMessage if actionRequiredMessage is not available
    const message = actionRequiredMessage || stripeAction || lastErrorMessage || 'Edit and fix the account details';
      
    return {
      state: 'error',
      statusText: 'Action Required',
      color: '#fa8c16', // orange
      dotColor: 'orange',
      message: message,
      showDot: true,
      stripeError: stripeError, // ✅ Include Stripe error for debugging
    };
  }
  
  // Deleted state (admin only - should be filtered in UI)
  if (status === 'deleted') {
    return {
      state: 'deleted',
      statusText: 'Deleted',
      color: '#000000', // black
      dotColor: 'default',
      message: null,
      showDot: true,
    };
  }
  
  // Paused state
  if (status === 'paused') {
    return {
      state: 'inactive',
      statusText: 'Inactive',
      color: '#ff4d4f', // red
      dotColor: 'red',
      message: null,
      showDot: true,
    };
  }
  
  // Ready status with provider (auto accounts) - should show as "In Progress"
  if (status === 'ready' && (provider || type)) {
    return {
      state: 'in-progress',
      statusText: 'In Progress',
      color: '#1890ff', // blue
      dotColor: 'blue',
      message: 'No action needed.',
      showDot: true,
    };
  }
  
  // Default/fallback states
  return {
    state: 'default',
    statusText: status || 'Ready',
    color: '#d9d9d9', // gray
    dotColor: 'default',
    message: null,
    showDot: false,
  };
};

/**
 * BalancesList - Shows balances with default bank accounts and Cash Out buttons
 * This component displays balances in a list format (not grid) with associated bank accounts
 */
export const BalancesList = ({ accounts, bankAccounts, client, onAddBankAccount, onCashOut, loading, withdrawals = [], clientId, isAdmin = false, isTransferToTeamAllowed = false, ...rest }) => {
  
  // Get account IDs for the hook
  const accountIds = accounts.map(acc => acc.availableAccountId).filter(Boolean);
  
  // Use real-time hook for last withdrawals
  const lastWithdrawals = useLastWithdrawalsByAccount(clientId, accountIds, withdrawals);
  
  // ProductSwitcher for product name lookup
  const productSwitcher = new ProductSwitcher({});
  
  // Get last withdrawal for each account
  const getLastWithdrawalForAccount = (accountId) => {
    const filtered = withdrawals.filter(w => {
      return w.accountId === accountId && w.payout;
    });
    
    const result = filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
    
    return result;
  };
  
  // Get default (first live) bank account for each currency
  const getDefaultBankAccount = (currency) => {
    if (!bankAccounts || bankAccounts.length === 0) {
      return null;
    }
    
    const accounts = bankAccounts.filter(acc => {
      if (acc.currency !== currency) return false;
      // Don't hide retry accounts with errors - user needs to see them to fix
      // Server sets hide: true on retry accounts, but we still need to show them
      const isLive = acc.status === 'live';
      const isClassicReady = (!acc.provider && !acc.type && acc.status === 'ready');
      const isRetry = acc.status === 'retry'; // Include retry accounts (action required)
      return isLive || isClassicReady || isRetry;
    });
    
    // Return first live account (could add isDefault flag later)
    return accounts.length > 0 ? accounts[0] : null;
  };
  
  // Check if can add bank for currency
  const canAddBank = (currency) => {
    const check = canAddBankAccount(currency, client);
    return check.allowed;
  };

  // Check if bank account has action required (retry status with error severity)
  const hasActionRequired = (bankAccount) => {
    if (!bankAccount) return false;
    return bankAccount.status === 'retry' && bankAccount.severity === 'error';
  };
  
  // No tips yet: still show one row per currency this client can be paid in, at
  // zero, so a bank account can be added before the first tip arrives.
  const rows = (accounts && accounts.length > 0)
    ? accounts
    : placeholderAccountsFor(client);

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Custom layout instead of Ant Design List */}
      {rows.map((v) => {
          const currency = v.currency;
          const amount = v.available || 0;
          const pending = v.pending != null ? v.pending : null;
          const withdrawn = v.withdrawn || 0;
          const defaultBank = getDefaultBankAccount(currency);
          const canAdd = canAddBank(currency);
          const lastWithdrawal = lastWithdrawals[v.availableAccountId] || getLastWithdrawalForAccount(v.availableAccountId);
          
          // Clear, mutually exclusive state variables
          const hasBalance = amount > 0;
          const hasRetryError = defaultBank && defaultBank.status === 'retry' && defaultBank.severity === 'error';
          const hasWorkingAccount = defaultBank && (defaultBank.status === 'live' || defaultBank.status === 'ready');
          const hasNoAccount = !defaultBank;
          
          // Button states (mutually exclusive)
          const showEditAccount = hasRetryError;
          const showCashOut = hasWorkingAccount && hasBalance;
          const showAddAccount = hasNoAccount;
          const showDisabledCashOut = hasWorkingAccount && !hasBalance;
          
          // Middle area states (mutually exclusive)
          const showActionRequired = hasRetryError;
          const showWorkingAccount = hasWorkingAccount;
          const showNoAccountSetup = hasNoAccount;
          
          return (
            <div
              key={`${v.product}-${currency}`}
              style={{ 
                backgroundColor: '#fafafa',
                marginBottom: 16,
                padding: 24,
                borderRadius: 8,
                border: '1px solid #d9d9d9'
              }}
            >
              <div style={{ 
                display: 'flex', 
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row', // Changed breakpoint from 576 to 768
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                gap: 16
              }}>
                {/* Column 1: Balance Info */}
                <div style={{ 
                  flex: '1', 
                  textAlign: 'center',
                  minWidth: window.innerWidth <= 768 ? '100%' : '200px' // Changed breakpoint from 576 to 768
                }}>
                  <Space direction="vertical" size={4} align="center">
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 500, textAlign: 'left', width: '100%' }}>
                      Available {productSwitcher.getProductMap(v.product)?.pluralName || v.product}
                    </Text>
                    <Title level={3} style={{ margin: 0, color: amount > 0 ? '#52c41a' : '#8c8c8c', fontWeight: 'bold' }}>
                      <MoneyTitle 
                        amount={amount} 
                        currency={currency} 
                        digits={2} 
                      />
                    </Title>
                    
                    {/* Pending and Cash out in a card-like container */}
                    {(pending != null || withdrawn > 0) && (
                      <div style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        width: '100%',
                        border: '1px solid #e9ecef',
                        textAlign: 'left'
                      }}>
                        {pending != null && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <ClockCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', marginRight: '6px' }} />
                              <Text type="secondary" style={{ fontSize: 11, marginRight: '4px' }}>
                                Pending:
                              </Text>
                            </div>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              <MoneyTitle amount={pending} currency={currency} digits={2} />
                            </Text>
                          </div>
                        )}
                        {withdrawn > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <BarChartOutlined style={{ color: '#8c8c8c', fontSize: '12px', marginRight: '6px' }} />
                                <Text type="secondary" style={{ fontSize: 11, marginRight: '4px' }}>
                                  Paid out:
                                </Text>
                            </div>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              <MoneyTitle amount={withdrawn} currency={currency} digits={2} />
                            </Text>
                          </div>
                        )}
                      </div>
                    )}
                  </Space>
                </div>
                
                {/* Column 2: Bank Details */}
                <div style={{ 
                  flex: '1', 
                  textAlign: 'center',
                  minWidth: window.innerWidth <= 768 ? '100%' : '200px', // Changed breakpoint from 576 to 768
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%'
                }}>
                  {/* Action Required State */}
                  {showActionRequired && (
                    <div style={{ 
                      textAlign: 'center', 
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 4, textAlign: 'center', width: '100%' }}>
                        Default Payout Account
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 4, width: '100%' }}>
                        <ExclamationCircleOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
                        <span style={{ fontWeight: 'bold', textAlign: 'center', color: '#fa8c16' }}>Action Required</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#666', textAlign: 'center', width: '100%' }}>
                        Edit bank details to fix
                      </div>
                    </div>
                  )}

                  {/* Working Account State */}
                  {showWorkingAccount && (
                    <div style={{ 
                      textAlign: 'center', 
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 4, textAlign: 'center', width: '100%' }}>
                        Default Payout Account
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 4, width: '100%' }}>
                        <CheckCircleFilled style={{ color: '#52c41a', marginRight: 8 }} />
                        <span style={{ fontWeight: 'bold', textAlign: 'center' }}>{defaultBank.displayName}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#666', textAlign: 'center', width: '100%' }}>
                        {makeShortBankDescription(defaultBank)}
                      </div>
                    </div>
                  )}

                  {/* No Account Setup State */}
                  {showNoAccountSetup && (
                    <div style={{ 
                      textAlign: 'center', 
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 4, textAlign: 'center', width: '100%' }}>
                        Default Payout Account
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 4, width: '100%' }}>
                        <ExclamationCircleOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
                        <span style={{ color: '#666', textAlign: 'center' }}>No bank account set up</span>
                      </div>
                      {canAdd ? (
                        <button 
                          style={{ 
                            background: 'none', 
                            border: 'none', 
                            color: '#1890ff', 
                            fontSize: 12, 
                            cursor: 'pointer',
                            textAlign: 'center',
                            display: 'block',
                            margin: '0 auto',
                            width: '100%'
                          }}
                          onClick={() => onAddBankAccount && onAddBankAccount(currency)}
                        >
                          Add {currency} Bank Account
                        </button>
                      ) : (
                        <div style={{ fontSize: 12, color: '#666', textAlign: 'center', width: '100%' }}>
                          Add a bank account on your first cash out
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Column 3: Cash Out Button */}
                <div style={{ 
                  flex: '1', 
                  textAlign: 'center',
                  minWidth: window.innerWidth <= 768 ? '100%' : '200px' // Changed breakpoint from 576 to 768
                }}>
                  {/* Edit Account Button */}
                  {showEditAccount && (
                    <Button 
                      type="primary" 
                      size="large"
                      icon={<EditOutlined />}
                      onClick={() => onAddBankAccount && onAddBankAccount(currency)}
                      style={{ width: '100%', maxWidth: '150px' }}
                    >
                      Edit Account
                    </Button>
                  )}

                  {/* Cash Out Button */}
                  {showCashOut && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                      <Button 
                        type="primary" 
                        size="large"
                        icon={<BankOutlined />}
                        loading={loading}
                        onClick={() => onCashOut && onCashOut(currency, defaultBank, v.availableAccountId)}
                        style={{ width: '100%', maxWidth: '150px' }}
                      >
                        Cash Out
                      </Button>
                      {currency !== 'USD' && v.product === 'tipping' && amount > 0 && isTransferToTeamAllowed && (
                        <Button 
                          type="default" 
                          size="middle"
                          onClick={() => onCashOut && onCashOut(currency, defaultBank, v.availableAccountId, 'team')}
                          style={{ width: '100%', maxWidth: '150px' }}
                        >
                          Send to Team
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Add Account Button */}
                  {showAddAccount && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                      <Button 
                        type="primary" 
                        size="large"
                        icon={<PlusOutlined />}
                        onClick={() => onAddBankAccount && onAddBankAccount(currency)}
                        style={{ width: '100%', maxWidth: '150px' }}
                      >
                        Add Account
                      </Button>
                      {currency !== 'USD' && v.product === 'tipping' && hasBalance && isTransferToTeamAllowed && (
                        <Button 
                          type="default" 
                          size="middle"
                          onClick={() => onCashOut && onCashOut(currency, defaultBank, v.availableAccountId, 'team')}
                          style={{ width: '100%', maxWidth: '150px' }}
                        >
                          Send to Team
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Disabled Cash Out Button */}
                  {showDisabledCashOut && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                      <Tooltip title="No available balance">
                        <Button 
                          type="default" 
                          size="large" 
                          disabled
                          style={{ width: '100%', maxWidth: '150px' }}
                        >
                          Cash Out
                        </Button>
                      </Tooltip>
                      {currency !== 'USD' && v.product === 'tipping' && isTransferToTeamAllowed && (
                        <Tooltip title="No available balance">
                          <Button 
                            type="default" 
                            size="middle" 
                            disabled
                            style={{ width: '100%', maxWidth: '150px' }}
                          >
                            Send to Team
                          </Button>
                        </Tooltip>
                      )}
                      <div style={{ 
                        fontSize: 12, 
                        color: '#999', 
                        marginTop: 8,
                        textAlign: 'center'
                      }}>
                        No available balance
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Sub-content below main content */}
              <LastWithdrawalBox withdrawal={lastWithdrawal} clientId={clientId} isAdmin={isAdmin} />
            </div>
          );
        })}
      </div>
    );
};

export const BankAccountsList = ({ condition, onAddAccount, onEditAccount, onDeleteAccount, locStrings, client, data, isAdmin, accounts, ...rest }) => {

  // Filter data based on user role
  // Users: Only see live or retry status
  // Admin: See all (already filtered by bankFilter in BankClientProfileScreen)
  const filteredData = isAdmin ? data : (data || []).filter(item => {
    const { status, provider, type } = item || {};
    const isClassic = !provider && !type;
    // Show:
    // - live, retry for provider-managed
    // - classic accounts when status is ready (treated as active)
    return status === 'live' || status === 'retry' || (isClassic && status === 'ready');
  });

  // A bank account can be added before any tip arrives, in every country.
  const isAddButtonDisabled = false;

  const footerActionCard =  (
    <Space direction='horizontal'>
      <Button 
        key="1" 
        type="text" 
        icon={<PlusCircleOutlined />} 
        onClick={onAddAccount}
        disabled={isAddButtonDisabled}
      >
        Add a New Bank Account
      </Button>
    </Space>
  );

  const deleteAction = (item, isAdmin) => {
    const isHidden = item.hide ? item.hide : false;
    const actionText = item.hide ? 'Restore' : 'Delete';
    // Allow end users to delete (hide) any non-hidden account; only admins can restore
    const canUserDelete = (!isAdmin && !isHidden);
    const canAdminModify = isAdmin;
    const shouldShow = canAdminModify || canUserDelete;
    if (!shouldShow) return null;
    
    // Don't show Restore button for retry accounts (they need fixing, not restoring)
    if (isHidden && item.status === 'retry') return null;
    if (!isHidden) {
      // Deleting (hiding) - show Modal.confirm for consistent layout
      const showConfirm = () => {
        Modal.confirm({
          title: 'Delete bank account?',
          content: 'This will remove the bank account from your list. Contact support@strikepay.co if you need help to get it back.',
          okText: 'Delete',
          cancelText: 'Cancel',
          okButtonProps: { danger: true },
          centered: true,
          onOk: () => onDeleteAccount(item.id, !isHidden),
        });
      };
      return (
        <Button type='link' size='small' onClick={showConfirm}>Delete</Button>
      );
    }
    // Restoring (admin only) - no confirmation necessary
    return (
      <Button type='link' size='small' onClick={() => { onDeleteAccount(item.id, !isHidden) }}>Restore</Button>
    );
  }

  const actions = (item, isAdmin) => {
    let actionList = [];

    // Get display state based on status and severity
    const displayState = getPayoutDisplayState(item);
    const { statusText, color, dotColor, message, showDot } = displayState;

    if (item.provider && item.provider === 'moneyjar') {
      if(item.status && item.status === 'live') {
        actionList = [
          <Link key={`bank-li-acts-${item.id}-details`} to={`./bank/${item.id}`}>
            <Button type='default' size='small'>Details</Button>
          </Link>,
        ]  
      } else {
        // MoneyJar: Continue Setup button for non-live status
        actionList = [
          <Link key={`bank-li-acts-${item.id}-continue-setup`} to={`./bank/${item.id}`}>
            <Button type='primary' size='small'>Continue Setup</Button>
          </Link>,
        ]  
      }
    } else {
      // Non-MoneyJar providers (e.g., stripeus) - no Payouts button in list
      actionList = [];
    }

    // Add Edit button - always for admin, only for non-live status for clients
    const shouldShowEdit = isAdmin || (item.status !== 'live');
    if (shouldShowEdit) {
      actionList = [
        ...actionList,
        <Button key={`bank-li-acts-${item.id}-edit`} type='link' size='small' onClick={() => { onEditAccount(item.id) }}>Edit</Button>,  
      ];
    }

    // Admin-only buttons
    if(isAdmin) {
      actionList = [
        ...actionList,
        <Link key={`bank-li-acts-${item.id}-progress`} to={`./bank/${item.id}`}>
          <Button type='default' size='small'>Progress</Button>
        </Link>,
      ];
    }

    // Delete/Hide action for both admin and end user (with rules)
    const deleteActionResult = deleteAction(item, isAdmin);
    if (deleteActionResult) {
      actionList = [
        ...actionList,
        deleteActionResult,
      ];
    }


    // Add status with colored dot
    if (showDot) {
      actionList = [
        ...actionList,
        <Badge key={`bank-li-acts-${item.id}-status-badge`} status={dotColor} text={statusText} />,
      ];
    } else {
      actionList = [
        ...actionList,
        <Tag key={`bank-li-acts-${item.id}-status-tag`} color={color}>{statusText}</Tag>,
      ];
    }

    // Show message for in-progress and error states
    if (message) {
      actionList = [
        ...actionList,
        <span key={`bank-li-acts-${item.id}-message`} style={{ color: color, fontSize: '12px' }}>{message}</span>,
      ];
    }

    return [
      ...actionList,
    ];
  };

  return ( 
    <List
      itemLayout="vertical"
      size='large'
      align='left'
      loading={!condition()}
      header={footerActionCard}
      dataSource={filteredData}
      renderItem={item => (
        <List.Item actions={actions(item, isAdmin)} key={`bank-li-${item.id}`}>
          <List.Item.Meta
            avatar={iconForCCY(item.currency)}
            title={makeDisplayName(item)}
            description={makeBankDescription(item)}
          />
          { item.status === 'retry' && item.severity !== 'info' &&
            <Alert
              message="Action Required"
              description={
                item.stripeError || 
                item.errorMessage || 
                item.actionRequiredMessage || 
                'Please fix the bank account details to resolve the issue.'
              }
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
            />
          }
          {/* Admin-only debug information - only show for error states */}
              {isAdmin && item.status !== 'live' && item.status !== 'deleted' && (item.stripeError || item.stripeAction) &&
                <div style={{ 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '4px', 
                  padding: '8px', 
                  marginTop: '8px',
                  fontSize: '12px'
                }}>
                  <Text strong style={{ color: '#666' }}>Admin only:</Text>
                  {item.stripeError && (
                    <div><Text code>stripeError:</Text> {item.stripeError}</div>
                  )}
                  {item.stripeAction && (
                    <div><Text code>stripeAction:</Text> {item.stripeAction}</div>
                  )}
                </div>
              }
        </List.Item>
      )}
    />
  );
};

const PayoutListItem = ({ id, status, date, money, payout }) => {
  let color = (status === 'paid') ? 'gold' : 'green';
  let statusText = (status === 'paid') ? 'Paid' : 'Sent';
  const { currency } = money || {};
  const avatar = iconForCCY(currency);  
  const description = payout ? makeShortBankDescription(payout) : '';
  const title = payout ? makeDisplayName(payout) : null;

  switch(status) {
    case 'sent':
      statusText = 'Sent';
      color = 'green';
      break;
    case 'paid':
      statusText = 'Paid';
      color = 'darkgreen';
      break;
    case 'api_requested':
      statusText = 'Requested...';
      color = 'blue';
      break;
    case 'waiting':
      statusText = 'Check in 2-3 days...';
      color = 'green';
      break;
    case 'batch_requested':
      statusText = 'Check in 2-3 days...';
      color = 'green';
      break;
    case 'batched':
      statusText = 'Check in 2-3 days...';
      color = 'green';
      break;
    case 'refund_requested':
      statusText = 'Refunding...';
      color = 'green';
      break;
    case 'refund_failed':
      statusText = 'Contact Support!';
      color = 'red';
      break;
    case 'refunded':
      statusText = 'Cancelled payout';
      color = 'yellow';
      break;
    default:
      statusText = status;
  }

  return (
    <Space direction='vertical' size={'small'}>
      <Space direction='horizontal' size={'small'}>
        {avatar}
        <Space direction='vertical' size={'small'}>
        <Space direction='horizontal' spacing={0}>
          {title && <Text strong>{title}</Text>}
          {description && <Text>{description}</Text>}
        </Space>
          <Tag style={{ 
            background: '#fff', 
            borderColor: '#d9d9d9', 
            color: '#000', 
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
          }}>
            {`Strikepay - ${id}`}
          </Tag>
        </Space>
      </Space>
      <Space direction='horizontal'>
        <Tag color={color}>{statusText}</Tag>
        <Button 
          type='default' 
          size='small' 
          onClick={() => { window.location.href = `../../withdrawal/${id}`; }}
        >
          Detail
        </Button>
      </Space>
      <Text>{date}</Text>
    </Space>
  );
}

const PayoutListAmountItem = ({ amount, currency, status }) => {
  return (
    <MoneyTitle
      amount={amount} currency={currency} digits={2}
      strikethrough={reversedStatuses.includes(status) ? true : false}
    />
  );
}

export const PayoutsList = ({ condition, locStrings, payoutMethodId, withdrawals, ...rest }) => {
  const columns = [
    {
      title: 'Withdrawal',
      dataIndex: 'statuses',
      key: 'statuses',
      render: item => ( 
        <PayoutListItem {...item} />
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: item => (
        <PayoutListAmountItem {...item} />
      )
    },
  ];
  
  const tableData = [];
  let key = 1;
  withdrawals.forEach(withdrawal => {
    if (allowedStatuses.includes(withdrawal.status)) {
      const formattedAmount = `${ccy[withdrawal.currency]}${ (withdrawal.amount/100).toFixed(2)}`
      const formattedDate = moment(withdrawal.createdAt.toMillis()).calendar().toString();

      const payoutRow = {
        amount: { 
          amount: withdrawal.amount, currency: withdrawal.currency,
          status: withdrawal.status,
        },
        statuses: { 
          id: withdrawal.withdrawalId,
          date: formattedDate,
          status: withdrawal.status,
          amount: formattedAmount,
          clientId: withdrawal.clientId,
          money: { amount: withdrawal.amount, currency: withdrawal.currency },
          payout: withdrawal.payout,
        },
        date: formattedDate,
        key: `wt-tr-${withdrawal.withdrawalId}`,
      }

      key++;
      tableData.push(payoutRow);
    }
  });

  return ( 
    <Table 
      columns={columns} 
      dataSource={tableData} 
    />
  );

};


// Helper function for status dots - shared between components
const statusDot = (status) => {
  // success-ish
  const ok = ['paid', 'sent', 'api_requested', 'batch_requested', 'batched', 'waiting'].includes(status);
  const fail = ['failed', 'refund_failed'].includes(status);
  const color = ok ? 'green' : (fail ? 'red' : 'blue');
  return <Badge status={color} />;
};

// History/Withdrawals list styled like balances list
export const WithdrawalsList = ({ withdrawals = [], clientId, isAdmin = false, highlightWithdrawalId, expandedIds = new Set(), onToggleExpanded }) => {
  // Use expandedIds from parent component - no local state needed

  const amountNode = (amount, currency, status) => (
    <Title level={3} style={{ margin: 0, color: '#000' }}>
      <MoneyTitle amount={amount} currency={currency} digits={2} strikethrough={reversedStatuses.includes(status)} />
    </Title>
  );

  const handleRefund = async (withdrawalId, withdrawalData) => {
    const { amount, currency } = withdrawalData;
    const amountDisplay = (amount / 100).toFixed(2);
    
    Modal.confirm({
      title: 'Refund Withdrawal - Super Admin Action',
      content: `This is a super admin action. Make sure to check with tech team before saying yes!\n\nRefund amount: ${currency} ${amountDisplay}`,
      okText: 'Yes, Refund',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const StrikeWithdrawalModel = (await import('../../models/StrikeWithdrawalModel')).default;
          const withdrawalModel = new StrikeWithdrawalModel({ clientId, withdrawalId });
          await withdrawalModel.setWithdrawalStatusToRefundRequested();
          // The withdrawal list will automatically update due to real-time Firestore listeners
        } catch (error) {
          console.error('Error requesting refund:', error);
          // You might want to show a notification here
        }
      }
    });
  };

  return (
    <List
      dataSource={withdrawals}
      renderItem={(w) => {
        const id = w && w.withdrawalId ? w.withdrawalId : w.id;
        const status = w.status;
        const currency = w.currency;
        const amount = w.amount;
        const payout = w.payout || {};
        const bankTitle = payout && payout.displayName ? payout.displayName : 'Bank Account';
        const short = payout && payout.accountNumber ? `***${String(payout.accountNumber).slice(-4)}` : (payout && payout.iban ? `***${String(payout.iban).slice(-4)}` : '');
        return (
        <div
          key={`wd-${id}`}
          style={{ backgroundColor: '#fafafa', marginBottom: 16, padding: 24, borderRadius: 8, border: '1px solid #d9d9d9' }}
        >
          {/* Main content row */}
          <Row style={{ width: '100%' }} align="middle" gutter={16}>
            <Col xs={24} sm={12}>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Space>
                  {statusDot(status)}
                  <Text strong>{`${bankTitle} ${short}`}</Text>
                </Space>
                <Tag style={{ background: '#fff', borderColor: '#d9d9d9', color: '#000', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>
                  {`Strikepay - ${id}`}
                </Tag>
                       <Text type="secondary" style={{ fontSize: 11, color: '#666' }}>
                         {getStatusDateDisplay(w)}
                       </Text>
              </Space>
            </Col>
            <Col xs={24} sm={6} style={{ textAlign: 'center' }}>
              <Space direction="vertical" size={4} align="center">
                {amountNode(amount, currency, status)}
                <Tag 
                  color={
                    status === 'paid' ? '#52c41a' :
                    status === 'sent' || status === 'batch_requested' || status === 'batched' ? 'blue' :
                    status === 'refunded' ? '#ff4d4f' :
                    status === 'refund_requested' ? 'orange' :
                    status === 'failed' ? '#ff4d4f' :
                    'default'
                  }
                  style={{ fontSize: '11px' }}
                >
                  {status === 'paid' ? 'Paid' :
                   status === 'sent' || status === 'batch_requested' || status === 'batched' ? 'Sent' :
                   status === 'refunded' ? 'Refunded' :
                   status === 'refund_requested' ? 'Refunding...' :
                   status === 'failed' ? 'Failed' :
                   'In progress'}
                </Tag>
                <Text type="secondary" style={{ fontSize: '10px', color: '#999' }}>
                  {(() => {
                    let dateField;
                    if (status === 'paid') {
                      dateField = w.paidAt || w.updatedAt;
                    } else if (status === 'sent' || status === 'batch_requested' || status === 'batched') {
                      dateField = w.sentAt || w.createdAt;
                    } else if (status === 'refunded') {
                      dateField = w.refundedAt || w.updatedAt;
                    } else if (status === 'refund_requested') {
                      dateField = w.refundedAt || w.updatedAt;
                    } else if (status === 'failed') {
                      dateField = w.updatedAt || w.createdAt;
                    } else {
                      dateField = w.createdAt;
                    }
                    
                    if (dateField) {
                      const date = dateField.toDate ? dateField.toDate() : dateField;
                      return moment(date).format('MMM D, YYYY');
                    }
                    return '';
                  })()}
                </Text>
              </Space>
            </Col>
            <Col xs={24} sm={6} style={{ textAlign: 'center' }}>
              <Space direction="vertical" size={4} align="center">
                <Space size={8}>
                  <Button
                    type="default"
                    size="middle"
                    icon={expandedIds.has(id) ? <UpOutlined /> : <DownOutlined />}
                    onClick={() => onToggleExpanded && onToggleExpanded(id)}
                  >
                    {expandedIds.has(id) ? 'Less' : 'More ...'}
                  </Button>
                  {isAdmin && currency !== 'USD' && !['paid', 'failed', 'refunded', 'refund_requested'].includes(status) && (
                    <Button
                      type="primary"
                      danger
                      size="middle"
                      onClick={() => handleRefund(id, w)}
                    >
                      Refund
                    </Button>
                  )}
                </Space>
                {w.updatedAt && (
                  <Text type="secondary" style={{ fontSize: 10, color: '#999' }}>
                    Updated: {moment(w.updatedAt.toDate ? w.updatedAt.toDate() : w.updatedAt).format('MMM D, YYYY')}
                  </Text>
                )}
              </Space>
            </Col>
          </Row>

          {/* Expanded content - below the main content */}
          {expandedIds.has(id) && (
            <div style={{ 
              marginTop: 16, 
              paddingTop: 16, 
              borderTop: '1px solid #e0e0e0',
              width: '100%'
            }}>
              {/* Progress View as sub-detail element */}
              <div style={{ 
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #d9d9d9'
              }}>
                <WithdrawalProgressListView clientId={clientId} withdrawalId={id} isAdmin={isAdmin} />
              </div>

              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <Link to={isAdmin ? 
                  `/admin/clients/${clientId}/withdrawal/${id}?returnTo=history` : 
                  `/client/manage/${clientId}/withdrawal/${id}?returnTo=history`
                }>
                  <Button type="link" size="small">
                    Open details →
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
        );
      }}
    />
  );
};




